/**
 * RouteChangeTracker — fires GA4 + GTM pageview events on SPA route changes.
 *
 * Why this exists: React Router navigation doesn't reload the page, so the
 * `gtag('config', GA_ID)` call in index.html only fires once (on initial load).
 * Without this component, GA only counts the LANDING page per session — every
 * subsequent in-app navigation is invisible. Real bounce rates and per-page
 * pageviews end up massively underreported.
 *
 * What it does:
 *   1. Watches `useLocation()` for any path change
 *   2. Respects the same exclusion list as index.html (/leadboard, /team/, /refer/)
 *   3. Waits for <Helmet> to update document.title (which is async — pages that
 *      fetch dynamic data, like the GYA question detail page, may take 100-300ms
 *      to set their title). Without this wait, GA records the previous page's
 *      title against the new page's path. Critical for the 2,801 GYA question
 *      detail pages where each title is dynamic.
 *   4. Sends GA4 page_view event with the new path + (final) title
 *   5. Pushes a 'spa_pageview' event into the dataLayer for GTM-side tracking
 *
 * Renders nothing. Mount inside <Router> once near the top of the tree.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Same exclusion rules as in /public/index.html (keep both in sync)
const EXCLUDED_PREFIXES = ['/leadboard', '/team/', '/refer/'];
const SETTLE_TIMEOUT_MS = 1500;       // give Helmet up to 1.5s to update title
const POLL_INTERVAL_MS = 50;          // check title every 50ms

function isExcluded(pathname) {
  return EXCLUDED_PREFIXES.some((p) => pathname.startsWith(p));
}

/**
 * Resolves when document.title settles (no change for one poll interval) OR
 * SETTLE_TIMEOUT_MS elapses — whichever comes first. Returns the final title.
 */
function waitForTitleSettle() {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') return resolve('');
    const started = Date.now();
    let lastTitle = document.title;
    let stableCount = 0;
    const timer = setInterval(() => {
      const current = document.title;
      if (current === lastTitle) {
        stableCount += 1;
      } else {
        lastTitle = current;
        stableCount = 0;
      }
      // Resolve when title has been stable for 2 consecutive polls (100ms)
      // OR we hit the timeout (1.5s) — whichever first.
      if (stableCount >= 2 || (Date.now() - started) > SETTLE_TIMEOUT_MS) {
        clearInterval(timer);
        resolve(document.title || '');
      }
    }, POLL_INTERVAL_MS);
  });
}

export function RouteChangeTracker() {
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    const fullPath = location.pathname + location.search;

    // Respect the same gate as index.html — admin pages never fire pageviews.
    if (isExcluded(location.pathname)) return undefined;

    waitForTitleSettle().then((title) => {
      if (cancelled) return;

      // 1) GA4 — explicit page_view event with updated path + settled title
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        try {
          window.gtag('event', 'page_view', {
            page_path: fullPath,
            page_location: window.location.href,
            page_title: title,
            send_to: 'G-KYMCLPZQKT',
          });
        } catch (e) {
          // Silently swallow — analytics must never break the page.
        }
      }

      // 2) GTM dataLayer — virtual pageview for any GTM-side triggers
      if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
        try {
          window.dataLayer.push({
            event: 'spa_pageview',
            page_path: fullPath,
            page_location: window.location.href,
            page_title: title,
          });
        } catch (e) { /* no-op */ }
      }
    });

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search]);

  return null;
}
